import { useEffect, useState } from "react";
import { FileText, Mail, Phone, X, ExternalLink, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, asError } from "@/api/client";

const STATUS_STYLES = {
  new: "text-[var(--gold)] border-[var(--gold)] bg-amber-50",
  contacted: "text-blue-700 border-blue-300 bg-blue-50",
  closed: "text-[var(--green)] border-[var(--green)] bg-green-50",
};

export default function AdminFranchise() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [detail, setDetail] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/franchise")
      .then(({ data }) => setItems(data))
      .catch(() => toast.error("Failed to load leads"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const mark = async (sub, status) => {
    try {
      await api.patch(`/admin/franchise/${sub.id}/status`, null, { params: { status } });
      toast.success(`Marked as ${status}`);
      setItems((arr) => arr.map((x) => (x.id === sub.id ? { ...x, status } : x)));
      if (detail?.id === sub.id) setDetail({ ...detail, status });
    } catch (e) { toast.error(asError(e)); }
  };

  const fmt = (iso) => new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

  const filtered = items.filter((x) => statusFilter === "all" || x.status === statusFilter);
  const newCount = items.filter((x) => x.status === "new").length;

  return (
    <div data-testid="admin-franchise">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-[var(--ink-2)]">
          <span className="font-semibold text-[var(--ink)]">{items.length}</span> total leads ·{" "}
          <span className="font-semibold text-[var(--gold)]">{newCount}</span> awaiting review
        </div>
        <div className="flex gap-2 flex-wrap">
          {["all", "new", "contacted", "closed"].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3.5 py-1.5 text-[11px] uppercase tracking-widest font-semibold rounded-full border transition ${
                statusFilter === s
                  ? "bg-[var(--green)] text-white border-[var(--green)]"
                  : "bg-white text-[var(--ink-2)] border-[#e5dec9] hover:border-[var(--green)]"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-[#e5dec9] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg-cream)] text-[var(--ink-3)] uppercase text-[10px] tracking-widest">
            <tr>
              <th className="text-left px-4 py-3 w-12">S.No</th>
              <th className="text-left">Applicant</th>
              <th className="text-left">Contact</th>
              <th className="text-left">City</th>
              <th className="text-left">Interest</th>
              <th className="text-left">Submitted</th>
              <th className="text-center">Status</th>
              <th className="text-right px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="text-center py-10 text-[var(--ink-3)] kbd text-xs">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={8} className="text-center py-12 text-[var(--ink-3)]">
                <FileText size={28} className="mx-auto text-[var(--gold)] mb-2" />
                No franchise enquiries{statusFilter !== "all" ? ` marked “${statusFilter}”` : " yet"}.
              </td></tr>
            )}
            {filtered.map((s, idx) => (
              <tr key={s.id} data-testid={`admin-franchise-row-${s.id}`} className="border-t border-[#f0e7d3] hover:bg-[var(--bg-cream)]/40 align-top">
                <td className="px-4 py-3 kbd text-[0.7rem] text-[var(--ink-3)]">{idx + 1}</td>
                <td className="py-3 font-semibold text-[var(--ink)]">{s.name}</td>
                <td className="py-3">
                  <a href={`mailto:${s.email}`} className="inline-flex items-center gap-1.5 text-[var(--green)] hover:underline text-xs"><Mail size={11} />{s.email}</a>
                  <div className="text-[var(--ink-3)] text-xs mt-0.5 inline-flex items-center gap-1.5"><Phone size={11} />{s.phone}</div>
                </td>
                <td className="text-[var(--ink-2)]">{s.city}</td>
                <td className="text-[var(--ink-2)] max-w-[220px] truncate" title={s.interest}>{s.interest}</td>
                <td className="text-[var(--ink-2)] text-xs">{fmt(s.created_at)}</td>
                <td className="text-center">
                  <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full border ${STATUS_STYLES[s.status] || "border-gray-300 text-gray-600"}`}>
                    {s.status}
                  </span>
                </td>
                <td className="px-4 text-right whitespace-nowrap">
                  <button
                    data-testid={`admin-franchise-view-${s.id}`}
                    onClick={() => setDetail(s)}
                    className="text-xs text-[var(--green)] hover:underline"
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detail && <FranchiseDetail sub={detail} onClose={() => setDetail(null)} onMark={mark} />}
    </div>
  );
}

function FranchiseDetail({ sub, onClose, onMark }) {
  return (
    <div className="fixed inset-0 z-[80] flex">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative ml-auto bg-white w-full max-w-md h-full overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e5dec9] sticky top-0 bg-white">
          <div>
            <div className="kbd text-[0.6rem] text-[var(--ink-3)]">Franchise Lead</div>
            <h3 className="font-serif-display text-lg text-[var(--ink)]">{sub.name}</h3>
          </div>
          <button onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-5 text-sm">
          <KV k="Email"><a href={`mailto:${sub.email}`} className="text-[var(--green)] hover:underline inline-flex items-center gap-1.5"><Mail size={11} />{sub.email}</a></KV>
          <KV k="Phone"><a href={`tel:${sub.phone}`} className="text-[var(--green)] hover:underline inline-flex items-center gap-1.5"><Phone size={11} />{sub.phone}</a></KV>
          <KV k="City">{sub.city}</KV>
          <KV k="Interest">{sub.interest}</KV>
          {sub.message && (
            <div>
              <Lbl>Message</Lbl>
              <p className="text-[var(--ink-2)] leading-relaxed bg-[var(--bg-cream)] rounded-md p-3 whitespace-pre-line">{sub.message}</p>
            </div>
          )}
          <KV k="Status">
            <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full border ${STATUS_STYLES[sub.status]}`}>{sub.status}</span>
          </KV>
          <div className="flex gap-3 pt-3 border-t border-[#e5dec9]">
            {sub.status !== "contacted" && (
              <button onClick={() => onMark(sub, "contacted")} className="btn-outline !py-1.5 text-xs"><ExternalLink size={11} /> Mark contacted</button>
            )}
            {sub.status !== "closed" && (
              <button onClick={() => onMark(sub, "closed")} className="btn-primary !py-1.5 text-xs"><CheckCircle2 size={11} /> Mark closed</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Lbl({ children }) { return <label className="kbd text-[0.6rem] text-[var(--ink-3)] mb-1.5 block">{children}</label>; }
function KV({ k, children }) { return <div><Lbl>{k}</Lbl><div className="text-[var(--ink)]">{children}</div></div>; }
